import React, { useState } from "react";
import Popover from "@/components/Popover";
import HeaderButton from "@/pages/my_exams/components/HeaderButton";
import { ClockIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/button";

// Icon components

const ChevronDown = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="#222"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="inline-block align-middle ml-1"
  >
    <polyline points="4,6 8,10 12,6" />
  </svg>
);

const ChevronUp = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="#222"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="inline-block align-middle ml-1"
  >
    <polyline points="4,10 8,6 12,10" />
  </svg>
);

const CalculatorIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#222"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="2" width="18" height="20" rx="2" fill="none" />
    <rect x="7" y="5" width="10" height="3" rx="1" fill="none" />
    <circle cx="7.5" cy="11.5" r="1" />
    <circle cx="12" cy="11.5" r="1" />
    <circle cx="16.5" cy="11.5" r="1" />
    <circle cx="7.5" cy="16" r="1" />
    <circle cx="12" cy="16" r="1" />
    <circle cx="16.5" cy="16" r="1" />
  </svg>
);

const DotsVerticalIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#222"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="6" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="12" cy="18" r="1.5" />
  </svg>
);

export interface ExamHeaderUIProps {
  // Display data
  sectionTitle: string;
  timer?: string;
  timerHidden?: boolean;
  timerColor?: string;

  // Directions content
  directionsContent?: React.ReactNode;

  // Calculator
  calculatorOpen?: boolean;
  isCalculatorAllowed?: boolean;

  // More menu
  moreOpen?: boolean;
  moreMenu?: React.ReactNode;

  // Event handlers
  onToggleTimerHidden?: () => void;
  onToggleCalculator?: () => void;
  onToggleMore?: () => void;
  onSwitchModule?: () => void;

  // Mode-specific props
  mode?: "exam" | "result" | "demo";
  showControls?: boolean;
}

const ExamHeaderUI: React.FC<ExamHeaderUIProps> = ({
  sectionTitle,
  timer,
  timerHidden = false,
  timerColor = "text-[#22336b]",
  directionsContent,
  calculatorOpen = false,
  isCalculatorAllowed = false,
  moreOpen = false,
  moreMenu,
  onToggleTimerHidden,
  onToggleCalculator,
  onToggleMore,
  onSwitchModule,
  mode = "exam",
  showControls = true,
}) => {
  const [directionsOpen, setDirectionsOpen] = useState(false);

  return (
    <div className="grid grid-cols-[1fr_auto_auto] items-center px-8 py-3 bg-[#e6edf8] custom-dashed-border">
      {/* Left: Section title + Directions + Switch Module */}
      <div className="flex flex-col items-start justify-center relative">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-lg font-bold text-[#22336b] tracking-tight text-left">
            {sectionTitle}
          </div>
          {(mode === "result" || mode === "demo") && onSwitchModule && (
            <Button onClick={onSwitchModule} className="text-xs">
              Change
            </Button>
          )}
        </div>
        {directionsContent && (
          <Popover
            className="max-w-[880px] font-serif"
            open={directionsOpen}
            onOpenChange={setDirectionsOpen}
            placement="bottom-start"
            title="Directions"
            trigger={
              <button className="text-sm text-black font-medium flex items-center mt-1 px-0 py-0 focus:outline-none cursor-pointer">
                <span className="flex items-center">
                  Directions{directionsOpen ? <ChevronUp /> : <ChevronDown />}
                </span>
              </button>
            }
          >
            {directionsContent}
          </Popover>
        )}
      </div>

      {/* Center: Timer */}
      <div className="flex items-center justify-center">
        {timer && showControls && (
          <div className="flex items-center justify-center">
            <button
              className={`text-2xl font-bold ${timerColor} ${
                timerHidden ? "opacity-30" : ""
              } flex items-center cursor-pointer`}
              onClick={onToggleTimerHidden}
            >
              <ClockIcon className="w-6 h-6 mr-2" />
              {timerHidden ? "••:••" : timer}
            </button>
          </div>
        )}
      </div>

      {/* Right: Controls */}
      <div className="flex items-center justify-end gap-2">
        {showControls && (
          <>
            {/* Calculator Button */}
            {isCalculatorAllowed && (
              <HeaderButton
                icon={<CalculatorIcon />}
                label="Calculator"
                onClick={onToggleCalculator || (() => {})}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ExamHeaderUI;
